import { toConstraints, type DevicePreferences } from "@centvox/shared/media";

/**
 * ⭐ **`getUserMedia` ELE ALMA — TEK NOKTA.**
 *
 * ### 🐞 Sahada ne oldu?
 * Mobilde kullanıcı kamera/mikrofon iznini **verdiği hâlde** "izin verilmedi"
 * uyarısı görüyordu. Neden: çağrı `deviceId: { exact: … }` ile yapılıyor ve
 * `getUserMedia` bu cihaz yoksa `OverconstrainedError` fırlatıyor. Çağıran
 * taraf ise `NotFoundError` dışındaki **her hatayı** "izin reddedildi" sayıyordu.
 *
 * Kaydedilmiş cihaz kimliğinin geçersizleşmesi istisna değil, **kural**:
 * - iOS Safari cihaz kimliklerini oturum/köken başına yeniden üretir,
 * - kullanıcı aynı hesabı başka bir telefonda açar,
 * - kulaklık çıkarılır, kamera başka bir uygulamaya geçer.
 *
 * Sonuç: kullanıcı olmayan bir ayarı aramaya gönderiliyor, kamerası hiç
 * açılmıyordu. Üstelik `reconcileDevices` (ölü kimlik temizleyicisi) yalnız
 * **başarılı** bir `getUserMedia`'dan SONRA çalıştığı için bu duruma hiç
 * ulaşılamıyordu — kilitlenme kendi kendini onaramıyordu.
 *
 * ### Çözüm: tek adımlık gevşetme merdiveni
 * ```
 * 1) tam tercih (exact deviceId)   → olmazsa
 * 2) cihaz kimlikleri OLMADAN      → tarayıcı varsayılan cihazı seçer
 * ```
 * İkinci adım başarılıysa çağırana `relaxed: true` döner; çağıran kaydedilmiş
 * kimlikleri **temizler**, böylece arıza bir sonraki açılışta tekrarlamaz.
 *
 * ⚠️ Merdiven `NotAllowedError`de İŞLETİLMEZ: izin gerçekten reddedilmişse
 *    ikinci bir istem kullanıcıyı taciz eder ve tarayıcı çoğu zaman istemi
 *    hiç göstermeden yine reddeder.
 */

/** İzin isteminin gerçek sonucu — hepsi "reddedildi" DEĞİLDİR. */
export type MediaFailure =
  /** Kullanıcı (veya kurumsal politika) reddetti. */
  | "denied"
  /** Cihaz yok: kamerasız/mikrofonsuz katılım hâlâ mümkündür. */
  | "notFound"
  /** Cihaz başka bir uygulama/sekme tarafından tutuluyor. */
  | "busy"
  /** Güvenli olmayan köken (HTTP) veya API hiç yok. */
  | "unsupported"
  | "unknown";

export interface AcquireResult {
  stream: MediaStream;
  /** ⚠️ `true` ise çağıran kaydedilmiş cihaz kimliklerini TEMİZLEMELİDİR. */
  relaxed: boolean;
}

/**
 * Hatayı **kullanıcıya söylenebilir** bir nedene çevirir.
 *
 * ⚠️ Tarayıcılar aynı durum için farklı adlar kullanır (`NotReadableError` ~
 *    Firefox'ta `AbortError`); bu yüzden eşleme adlara göre yapılır ve
 *    tanınmayan her şey `unknown` olur — "denied" varsayımı, kullanıcıyı
 *    yanlış yere gönderdiği için en pahalı yanlıştı.
 */
export function classifyMediaError(e: unknown): MediaFailure {
  const name = e instanceof Error ? e.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "notFound";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "busy";
    case "TypeError":
      // `navigator.mediaDevices` yok → güvenli olmayan köken (http://) klasiği.
      return "unsupported";
    default:
      return "unknown";
  }
}

/** Kaydedilmiş cihaz kimliği istendi mi? Gevşetmenin anlamlı olduğu tek durum. */
function hasDevicePin(prefs: DevicePreferences): boolean {
  return prefs.audioDeviceId !== null || prefs.videoDeviceId !== null;
}

/**
 * ⚠️ **Katman merdivende KORUNUR, gevşetilmez.** Gevşetme yalnız *cihaz
 *    kimliğini* düşürür; çözünürlüğü de düşürmek, ölü bir kimlik yüzünden
 *    HD odaya giren kullanıcıyı sessizce 720p'ye mahkûm ederdi ve sebebi
 *    hiçbir yerde görünmezdi.
 *
 * ⚠️ HD kısıtları `ideal` olduğu için ikinci bir merdiven basamağına gerek
 *    yoktur: 1080p veremeyen kamera `OverconstrainedError` fırlatmaz, elinden
 *    geleni verir (bkz. `VIDEO_CONSTRAINTS_HD`).
 */
export async function acquireUserMedia(
  prefs: DevicePreferences,
  audio?: { noiseSuppression?: boolean; music?: boolean },
  tier: "standard" | "hd" = "standard",
): Promise<AcquireResult> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    // ⚠️ `classifyMediaError` bunu `unsupported` olarak tanır.
    throw new TypeError("mediaDevices_unavailable");
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(toConstraints(prefs, audio, tier));
    return { stream, relaxed: false };
  } catch (e) {
    const reason = classifyMediaError(e);
    /**
     * ⚠️ Yalnız **kısıt kaynaklı** hatalarda gevşetilir. `denied` ve `busy`
     *    cihaz kimliğiyle ilgili değildir; orada ikinci deneme yalnız gecikme
     *    ve ikinci bir izin istemi üretir.
     */
    const constraintish = e instanceof Error && e.name === "OverconstrainedError";
    if (!hasDevicePin(prefs) || !(constraintish || reason === "notFound")) throw e;

    const stream = await navigator.mediaDevices.getUserMedia(
      toConstraints({ ...prefs, audioDeviceId: null, videoDeviceId: null }, audio, tier),
    );
    return { stream, relaxed: true };
  }
}
